/**
 * Isomorphic fetch wrapper with Result type.
 * Safe for both browser and Cloudflare Workers.
 */

export type FetchError =
  | { type: "timeout" }
  | { type: "rate_limit"; retryAfter?: number }
  | { type: "network"; message: string }
  | { type: "http"; body?: unknown };

export type Result<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: FetchError };

export interface FetchOptions extends Omit<RequestInit, "signal"> {
  timeout?: number;
}

export const API_BASE = "https://api.financeapis.dev/v1";

const DEFAULT_TIMEOUT = 60000; // One minute

export async function apiFetch<T>(
  url: string,
  options: FetchOptions = {},
): Promise<Result<T>> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (response.status === 429) {
      const retryAfter = response.headers.get("Retry-After");
      return {
        ok: false,
        status: 429,
        error: {
          type: "rate_limit",
          retryAfter: retryAfter ? parseInt(retryAfter, 10) : undefined,
        },
      };
    }

    if (!response.ok) {
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        // Response body not JSON, leave undefined
      }
      return {
        ok: false,
        status: response.status,
        error: { type: "http", body },
      };
    }

    return {
      ok: true,
      status: response.status,
      data: (await response.json()) as T,
    };
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === "AbortError") {
      return { ok: false, status: 408, error: { type: "timeout" } };
    }
    return {
      ok: false,
      status: 500,
      error: {
        type: "network",
        message: err instanceof Error ? err.message : "Unknown",
      },
    };
  }
}
